import React, { ReactElement, useCallback, useEffect, useState } from "react";

import EnterCupboard from "../EnterCupboard";
import Instructions from "../Instructions";
import IntroEmptyScene from "../IntroEmptyScene";
import { BlackOverlay } from "../IntroEmptyScene/components";
import IntroLastFrame from "../IntroLastFrame";
import IntroPopup from "../IntroPopup";

export type Props = {
  readonly onEndIntro: () => void;
  readonly onStartInstructions: () => void;
};

const GameIntro = ({
  onEndIntro,
  onStartInstructions,
}: Props): ReactElement => {
  // Intro states
  const [isInitialSceneVisible, setInitialSceneVisible] = useState(true);
  const [isIntroVideoVisible, setIntroVideoVisible] = useState(false);
  const [isCupboardVisible, setCupboardVisible] = useState(false);
  const [isIntroPopupVisible, setIsIntroPopupVisible] = useState(true);

  // Game states
  const [isInstructionsVisible, setInstructionsVisible] = useState(true);
  const [shouldGameShow, setGameShow] = useState(false);

  const initialVidRef: React.RefObject<HTMLVideoElement> = React.createRef();
  const instructionsVidRef: React.RefObject<HTMLVideoElement> =
    React.createRef();

  const onPlayIntroVideo = useCallback(() => {
    setIntroVideoVisible(true);
    setIsIntroPopupVisible(false);

    setTimeout(() => {
      setInitialSceneVisible(false);
    }, 1000);
  }, []);

  const playIntroVideo = useCallback(
    async () => await initialVidRef?.current?.play(),
    [initialVidRef]
  );

  useEffect(() => {
    if (isIntroPopupVisible) return;

    void (async () => {
      // Check whether video can be played on page load
      try {
        await playIntroVideo();
      } catch {
        // handle autoplay error by showing a play button
        setIsIntroPopupVisible(true);
        setIntroVideoVisible(false);
      }
    })();
  }, [initialVidRef, isIntroPopupVisible, playIntroVideo]);

  const showCupboard = useCallback(() => {
    setIntroVideoVisible(false);
    setCupboardVisible(true);
    setGameShow(true);
    onStartInstructions();
  }, [onStartInstructions]);

  const handleEndIntro = useCallback(() => {
    setInstructionsVisible(false);
    onEndIntro();
  }, [onEndIntro]);

  return (
    <>
      {/* Background visible to avoid flashes between state changes */}
      {isCupboardVisible ? <IntroLastFrame /> : <BlackOverlay />}

      {!shouldGameShow ? (
        <>
          {isInitialSceneVisible && (
            <IntroEmptyScene onClick={playIntroVideo} />
          )}
          <EnterCupboard
            onPlay={onPlayIntroVideo}
            onEnded={showCupboard}
            ref={initialVidRef}
            isVisible={isIntroVideoVisible}
          />

          {isIntroPopupVisible && <IntroPopup onClose={onPlayIntroVideo} />}
        </>
      ) : (
        <Instructions
          onEnded={handleEndIntro}
          ref={instructionsVidRef}
          isVisible={isInstructionsVisible}
        />
      )}
    </>
  );
};

export default GameIntro;
